import { Route, Routes } from '@angular/router'
import { HomePageComponent } from './home/home-page.component'
import { MDMAAggregationsComponent } from './public/mdma-aggegations/mdma-aggregations.component'
import { AuthGuard } from './shared/services/auth-guard.service'
import { IntervalUsageComponent } from './public/interval-usage/interval-usage.component'
import { RLSCertificatesComponent } from './public/rls-certificates/rls-certificates.component'
import { ImportErdComponent } from './public/import-erd/import-erd.component'
import { ServicePointGroupsComponent } from './public/service-point-groups/service-point-groups.component'
import { ServicePointManagementComponent } from './public/service-point-management/service-point-management.component'
import { IdrInvestigationComponent } from './idr-investigation/idr-investigation.component'
import { SettlementRunScheduleComponent } from './public/settlement-run-schedule/settlement-run-schedule.component'
import { EntityManagementComponent } from './entity-management/entity-management.component'
import { TBillSettlementsComponent } from './public/tbill-settlements/tbill-settlements.component'
import { defaultEntityTypeCode } from './entity-management/entity-metadata'
import { TBillRetailerUsageOverridesComponent } from './public/tbill-retailer-usage-overrides/tbill-retailer-usage-overrides.component'
import { TBillCACPHourOverridesComponent } from './public/tbill-ca-cp-hour-overrides/tbill-ca-cp-hour-overrides.component'
import { TBillSPChargesComponent } from './public/tbill-sp-charges/tbill-sp-charges.component'
import { TBillNITSRatesComponent } from './public/tbill-nits-rate/tbill-nits-rate.component'
import { TBillSchedule1Component } from './public/tbill-schedule-1/tbill-schedule-1.component'
import { Type } from '@angular/core'

const rootPaths = ['settlement-process', 'data-exploration', 'data-management', 'system-configuration'] as const

export type RootPath = typeof rootPaths[number]

// IMPORTANT - these values are used in dashboard definitions
// they cannot be changed without updating the matching dashboard defs 
type NavGroupTitle = 'Settlement Process' | 'Data Exploration' | 'Data Management' | 'System Configuration'

export type NavGroup = Readonly<{
  root: RootPath
  title: NavGroupTitle
}>

const settlementGroup: NavGroup = { root: 'settlement-process', title: 'Settlement Process' }

const dataExplorationGroup: NavGroup = { root: 'data-exploration', title: 'Data Exploration' }

const dataManagementGroup: NavGroup = { root: 'data-management', title: 'Data Management' }

const systemConfigGroup: NavGroup = { root: 'system-configuration', title: 'System Configuration' }

export const navGroups = {
  settlement: settlementGroup,
  dataExploration: dataExplorationGroup,
  systemConfig: systemConfigGroup,
  dataManagement: dataManagementGroup,
}

export type RouteDef = Readonly<{
  root: RootPath
  relativePath: string
  component?: Type<unknown>
  title?: string
  argPath?: string
  redirectTo?: string
  menuPosition?: 'start' | 'end' | 'none'
}>

const commercialModelRoutes = (): readonly RouteDef[] => {
  const common: RouteDef = {
    root: dataManagementGroup.root,
    relativePath: 'commercial-model',
    title: 'Commercial Model',
  }
  const component = EntityManagementComponent
  const argPath =  ':type/:entity'

  return [
    { ...common, argPath, component, menuPosition: 'none' },
    { ...common, argPath: `${argPath}/:relationshipType/:relatedEntityType`, component, menuPosition: 'none' },
    { ...common, redirectTo: `/${common.root}/${common.relativePath}/${defaultEntityTypeCode}/all`,  menuPosition: 'end' },
  ]
}

export const routeDefs: readonly RouteDef[] = [
  {
    root: settlementGroup.root,
    relativePath: 'mdma-settlement-runs',
    component: MDMAAggregationsComponent,
    title: 'MDMA Settlement Runs',
  },
  {
    root: settlementGroup.root,
    relativePath: 'tbill-settlement-runs',
    component: TBillSettlementsComponent,
    title: 'TBill Settlement Runs',
  },
  {
    root: settlementGroup.root,
    relativePath: 'settlement-run-schedule',
    component: SettlementRunScheduleComponent,
    title: 'Settlement Run Schedule',
  },
  {
    root: dataExplorationGroup.root,
    relativePath: 'idr-investigation',
    component: IdrInvestigationComponent,
    title: 'IDR Investigation',
    menuPosition: 'end',
  },
  {
    root: dataManagementGroup.root,
    relativePath: 'service-point-management',
    component: ServicePointManagementComponent,
    title: 'Service Point Management',
  },
  {
    root: dataManagementGroup.root,
    relativePath: 'interval-usage',
    component: IntervalUsageComponent,
    title: 'Interval Usage',
  },
  {
    root: dataManagementGroup.root,
    relativePath: 'tbill-schedule-1',
    component: TBillSchedule1Component,
    title: 'Schedule 1',
  },
  {
    root: dataManagementGroup.root,
    relativePath: 'tbill-schedule-9',
    component: TBillNITSRatesComponent,
    title: 'Schedule 9 (NITS)',
  },
  {
    root: dataManagementGroup.root,
    relativePath: 'tbill-ca-cp-hour-overrides',
    component: TBillCACPHourOverridesComponent,
    title: 'TBill Control Area Coincident Peak Hour Overrides',
  },
  {
    root: dataManagementGroup.root,
    relativePath: 'tbill-retailer-usage-overrides',
    component: TBillRetailerUsageOverridesComponent,
    title: 'TBill Retailer Usage Overrides',
  },
  {
    root: dataManagementGroup.root,
    relativePath: 'tbill-sp-charges',
    component: TBillSPChargesComponent,
    title: 'TBill SP Charges',
  },
  {
    root: dataManagementGroup.root,
    relativePath: 'expected-results-data',
    component: ImportErdComponent,
    title: 'Expected Results Data',
  },
  {
    root: dataManagementGroup.root,
    relativePath: 'service-point-groups',
    component: ServicePointGroupsComponent,
    title: 'Service Point Groups',
    menuPosition: 'end',
  },
  ...commercialModelRoutes(),
  {
    root: systemConfigGroup.root,
    relativePath: 'digital-certs',
    component: RLSCertificatesComponent,
    title: 'Digital Certificates',
  },
]

const createRoute = (routeDef: RouteDef): Route => {
  const { root, relativePath, argPath, component, redirectTo } = routeDef
  return {
    path: [root, relativePath, argPath].filter(Boolean).join('/'),
    component,
    ...redirectTo ? { redirectTo, pathMatch: 'full' } : { canActivate: [AuthGuard] },
    data: { routeDef },
  }
}

export const routes: Routes = [
  { path: '', component: HomePageComponent, canActivate: [AuthGuard] },
  ...routeDefs.map(createRoute),
]

const getRouteDefsFor = (root: RootPath): readonly RouteDef[] =>
  routeDefs.filter(_ => _.root === root)

export type RouteDefsByRoot = Readonly<Record<RootPath, readonly RouteDef[]>>

const getRouteDefsByRoot = (): RouteDefsByRoot => {
  const result: Partial<Record<RootPath, readonly RouteDef[]>> = {}
  rootPaths.forEach(rootPath => {
    result[rootPath] = getRouteDefsFor(rootPath)
  })
  return result as RouteDefsByRoot
}

export const routeDefsByRoot: RouteDefsByRoot = getRouteDefsByRoot()
